import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Job, CVUpload } from '../types';
import Layout from '../components/Layout';
import { MdWork, MdPerson, MdCheckCircle } from 'react-icons/md';
import { 
  HiArrowLeft, HiCalendar, HiFolder, HiCloudArrowUp, 
  HiExclamationCircle, HiClock, HiDocumentText, HiChartBar 
} from 'react-icons/hi2';

const JobDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [cvs, setCvs] = useState<CVUpload[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchJobDetails();
      fetchJobCVs();
    }
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getJob(Number(id));
      if (response.success) {
        setJob(response.data);
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobCVs = async () => {
    try {
      const response = await apiService.getJobCVs(Number(id));
      if (response && response.success && response.data) {
        setCvs(response.data.results);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      uploaded: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: HiCloudArrowUp },
      queued: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', icon: HiClock },
      analyzing: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: HiChartBar },
      analyzed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: MdCheckCircle },
      failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: HiExclamationCircle },
    };
    const style = styles[status as keyof typeof styles] || styles.uploaded;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-screen items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <div className="flex h-screen items-center justify-center">
          <p className="text-gray-600 dark:text-gray-400">Job not found</p>
        </div>
      </Layout>
    );
  }

  const stats = job.stats || {
    total_cvs: 0,
    analyzed: 0,
    pending: 0,
    uploaded: 0,
    queued: 0,
    analyzing: 0,
    failed: 0,
    total_size_bytes: 0,
    total_size_mb: 0,
  };

  return (
    <Layout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/jobs')}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
          >
            <HiArrowLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-navy-700 dark:text-white">{job.title}</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Job Details and CV Analysis
            </p>
          </div>
        </div>
      </div>

      {/* Job Info Card */}
      <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Job Information</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <MdPerson className="mr-3 mt-1 h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Created By</p>
                  <p className="font-medium text-navy-700 dark:text-white">{job.owner_name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{job.owner_email}</p>
                </div>
              </div>
              <div className="flex items-start">
                <HiCalendar className="mr-3 mt-1 h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
                  <p className="font-medium text-navy-700 dark:text-white">{formatDate(job.created_at)}</p>
                </div>
              </div>
              <div className="flex items-start">
                <MdWork className="mr-3 mt-1 h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                  <p className="font-medium text-navy-700 dark:text-white capitalize">{job.status}</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Description</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">{job.description}</p>
            {job.skills && job.skills.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">Required Skills:</p>
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="rounded-full bg-brand-500/10 px-3 py-1 text-xs font-medium text-brand-600 dark:text-brand-400"
                    >
                      {skill.name} (P{skill.priority})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CV Statistics Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Total CVs</p>
              <p className="mt-2 text-3xl font-bold">{stats.total_cvs}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <HiCloudArrowUp className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-gradient-to-br from-green-500 to-green-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Analyzed</p>
              <p className="mt-2 text-3xl font-bold">{stats.analyzed}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <MdCheckCircle className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Pending</p>
              <p className="mt-2 text-3xl font-bold">{stats.pending}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <HiClock className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Total Size</p>
              <p className="mt-2 text-3xl font-bold">{stats.total_size_mb}</p>
              <p className="text-xs opacity-75">MB</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <HiFolder className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
        <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">CV Status Breakdown</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
            <p className="text-sm text-blue-600 dark:text-blue-400">Uploaded</p>
            <p className="mt-1 text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.uploaded}</p>
          </div>
          <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
            <p className="text-sm text-yellow-600 dark:text-yellow-400">Queued</p>
            <p className="mt-1 text-2xl font-bold text-yellow-700 dark:text-yellow-300">{stats.queued}</p>
          </div>
          <div className="rounded-lg bg-purple-50 p-4 dark:bg-purple-900/20">
            <p className="text-sm text-purple-600 dark:text-purple-400">Analyzing</p>
            <p className="mt-1 text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.analyzing}</p>
          </div>
          <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
            <p className="text-sm text-green-600 dark:text-green-400">Analyzed</p>
            <p className="mt-1 text-2xl font-bold text-green-700 dark:text-green-300">{stats.analyzed}</p>
          </div>
          <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
            <p className="text-sm text-red-600 dark:text-red-400">Failed</p>
            <p className="mt-1 text-2xl font-bold text-red-700 dark:text-red-300">{stats.failed}</p>
          </div>
        </div>
      </div>

      {/* CV List */}
      <div className="rounded-xl bg-white shadow-lg dark:bg-navy-800">
        <div className="border-b border-gray-200 p-6 dark:border-navy-600">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Uploaded CVs ({cvs.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-navy-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  File Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Uploaded By
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Score
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Size
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Uploaded
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-navy-600">
              {cvs.map((cv) => (
                <tr key={cv.id} className="hover:bg-gray-50 dark:hover:bg-navy-700/50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500">
                        <HiDocumentText className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-navy-700 dark:text-white">{cv.filename_original}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{cv.mime}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-navy-700 dark:text-white">{cv.uploader_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{cv.uploader_email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(cv.status)}
                  </td>
                  <td className="px-6 py-4">
                    {cv.is_analyzed && cv.score !== null ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        {cv.score}/100
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatBytes(cv.size_bytes)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <HiCalendar className="mr-1 h-4 w-4 text-gray-400" />
                      {formatDate(cv.created_at)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {cvs.length === 0 && (
            <div className="py-12 text-center">
              <HiCloudArrowUp className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">No CVs uploaded yet</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default JobDetails;
